import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

export type WorkshopMode = 'simple' | 'advanced'

interface WorkshopModeValue {
  mode: WorkshopMode
  setMode: (m: WorkshopMode) => void
  toggle: () => void
}

const STORAGE_KEY = 'workshop.mode'
const WorkshopModeContext = createContext<WorkshopModeValue | null>(null)

function readInitial(): WorkshopMode {
  return localStorage.getItem(STORAGE_KEY) === 'advanced' ? 'advanced' : 'simple'
}

export function WorkshopModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<WorkshopMode>(readInitial)
  const setMode = useCallback((m: WorkshopMode) => {
    setModeState(m)
    localStorage.setItem(STORAGE_KEY, m)
  }, [])
  const toggle = useCallback(
    () => setMode(readInitial() === 'advanced' ? 'simple' : 'advanced'),
    [setMode],
  )
  return (
    <WorkshopModeContext.Provider value={{ mode, setMode, toggle }}>
      {children}
    </WorkshopModeContext.Provider>
  )
}

export function useWorkshopMode(): WorkshopModeValue {
  const ctx = useContext(WorkshopModeContext)
  if (!ctx) throw new Error('useWorkshopMode must be used within WorkshopModeProvider')
  return ctx
}
